<script setup lang="ts">
import type { LevelCardData } from "../model/types";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import LocLink from "~/components/lang/LocLink.vue";

defineProps<{
  cardData: LevelCardData,
}>();

const emit = defineEmits<{
  (e: "navigationLinkClicked", payload: { id: string, to: string }): void
}>();
</script>

<template>
  <div class="level-card-wrapper">
    <div class="level-card__main">
      <h2 class="text-20-600">{{ cardData.title }}</h2>
      <p class="text-body">{{ cardData.subtitle }}</p>
      <hr class="level-card__divider">
      <div class="level-card__main-description" v-for="description in cardData.descriptions">
        <BaseIcon
          icon="listPoint"
          height="16"
          width="16"
          class="level-card__main-description--icon" />
        <div class="level-card__main-description-text">
          <p class="text-body">{{ description.text }}</p>
          <template
            v-if="description.links?.length"
            v-for="link in description.links"
          >
            <LocLink :to="link.to" @click="emit('navigationLinkClicked', { id: cardData.id, to: link.to })">
              <div class="level-card__main-description-link">
                <span class="text-16-500">{{ link.text }}</span>
                <BaseIcon
                  class="level-card__main-description-link--icon"
                  icon="arrow"
                  height="24"
                  width="24" />
              </div>
            </LocLink>
          </template>
        </div>
      </div>
    </div>
    <div class="level-card__misc">
      <hr class="level-card__divider">
      <p class="text-body">{{ cardData.misc }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "assets/styles/global/colors" as c;
@use "assets/styles/global/mixins" as m;
@use "assets/styles/global/variables" as v;

.level-card-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 16px;
  background-color: c.$white;
  border-radius: 20px;

  width: 75vw;

  @media screen and (min-width: v.$tablet) {
    max-width: 346px;
  }

  @media screen and (min-width: v.$desktop) {
    max-width: 234px;
  }

  @media screen and (min-width: v.$desktop-1440) {
    max-width: 250px;
  }
}

.level-card {
  &__main {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-description {
      display: flex;
      gap: 4px;

      &--icon {
        flex-shrink: 0;
        margin-top: 4px;
      }

      &-text {
        display: flex;
        flex-direction: column;
      }

      &-link {
        display: flex;
        justify-content: space-between;
        align-items: center;

        & span {
          color: c.$secondary;
        }

        &--icon {
          flex-shrink: 0;
          fill: c.$secondary;
        }
      }
    }
  }

  &__divider {
    height: 1px;
    background-color: c.$secondGrey;
  }

  &__misc {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}
</style>